// Check provider API keys for the research wizards — live, one request per key, every visit.
//
// Each key is checked by its own POST /profile/user-preferences/test-api-keys/{key} so the toggles
// come alive one by one as answers arrive: a key still being checked reads as status 'checking'
// ("Verifying if source is active…"), and its toggle is held until then.
//
// Nothing is cached across visits (gcTime 0): a key that was just recharged or fixed is re-checked
// the next time the page opens. Within one visit the answer is shared by every component on the
// page (staleTime Infinity), so moving between wizard steps does not re-check.
//
// A check that fails outright (504 from a slow provider, a network blip) leaves that key without
// a result — "unknown" — which the gates treat as usable; the backend checks keys again at submit.
import { useQueries } from '@tanstack/react-query';
import { preferencesService } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import type { ApiKeyTestResult, OrgKeyName } from '@/types';
import { VERIFYING_REASON, type TestResults } from './apiKeyGating';

/** Every key the Target / Strategic wizards gate on (sources + enrichment). */
export const RESEARCH_KEYS: readonly OrgKeyName[] = [
  'apollo_api_key',
  'coresignal_api_key',
  'gmaps_api_key',
  'parallel_api_key',
  'datamagnet_api_key',
  'news_api_key',
  'serper_api_key',
  'openai_api_key',
  'anthropic_api_key',
  'gemini_api_key',
  'perplexity_api_key',
];

export interface ApiKeyStatus {
  /** Per-key verdicts; a key still being checked has status 'checking'. */
  testResults: TestResults;
  /** At least one key is still being checked (the page holds Start until all have answered). */
  isVerifying: boolean;
  /** At least one key's check failed outright (no verdict for it). */
  isError: boolean;
  /** Re-run the checks that failed. */
  refetch: () => void;
}

export function apiKeyStatusQueryKey(userId: string | null | undefined, key: OrgKeyName) {
  return ['apiKeyStatus', userId ?? 'anonymous', key] as const;
}

const CHECKING: ApiKeyTestResult = { status: 'checking', message: VERIFYING_REASON };

export function useApiKeyStatus(keys: readonly OrgKeyName[] = RESEARCH_KEYS): ApiKeyStatus {
  const userId = useAuthStore((s) => s.currentUser?.id);
  const queries = useQueries({
    queries: keys.map((key) => ({
      queryKey: apiKeyStatusQueryKey(userId, key),
      queryFn: async (): Promise<ApiKeyTestResult | null> => {
        const res = await preferencesService.testSingleApiKey(key);
        return res.testResults?.[key] ?? null;
      },
      // The toggles show their own "verifying" / retry state; no global error toast.
      meta: { silentError: true },
      staleTime: Infinity,
      gcTime: 0,
      retry: 1,
      refetchOnWindowFocus: false,
    })),
  });

  const testResults: TestResults = {};
  keys.forEach((key, i) => {
    const q = queries[i];
    if (q.data) testResults[key] = q.data;
    else if (q.isLoading) testResults[key] = CHECKING;
    // error / null → no entry → "unknown"
  });

  return {
    testResults,
    isVerifying: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError && q.data === undefined),
    refetch: () => {
      queries.forEach((q) => {
        if (q.isError) void q.refetch();
      });
    },
  };
}
